import { isFuture, parseISO } from 'date-fns';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Error from 'next/error';
import Head from 'next/head';

import { fetchTokenByID } from '@/apis/aniftyBackend/aniftyTokens/fetchTokenByID';
import { AniftyTokenDetailData } from '@/apis/aniftyBackend/aniftyTokens/interface';
import { isAxiosError } from '@/apis/aniftyBackend/client';
import { V2CreationData } from '@/apis/aniftyBackend/creations/interface';
import useFetchCreationIsLiked from '@/apis/aniftyBackend/creations/like/useFetchCreationIsLiked';
import useToggleMyLike from '@/apis/aniftyBackend/creations/like/useToggleMyLike';
import useV2FetchCreationByID from '@/apis/aniftyBackend/creations/useV2FetchCreationByID';
import v2FetchCreationByID from '@/apis/aniftyBackend/creations/v2FetchCreationByID';
import redirectTargets from '@/config/redirectTargets';
import routes from '@/config/routes';
import CreationDetail from '@/containers/creations/CreationDetail';
import useTryToAuthorize from '@/hooks/useTryToAuthorize';
import useWalletAddress from '@/hooks/useWalletAddress';
import { captureException } from '@/utils/Sentry';
import getCreationStatus from '@/utils/getCreationStatus';
import { convertImgixURL } from '@/utils/imgixUtils';

type Props =
  | {
      status: 'success';
      creation: V2CreationData;
      token: AniftyTokenDetailData | null;
    }
  | {
      status: 'error';
      errorCode: number;
    };

export const getStaticProps: GetStaticProps<Props> = async ({
  locale,
  params,
}) => {
  const { creationID: creationIDString } = params;

  if (typeof creationIDString !== 'string') {
    return {
      notFound: true,
    };
  }

  try {
    const creation = await v2FetchCreationByID(Number(creationIDString));

    if (!creation) {
      return {
        notFound: true,
      };
    }

    // Redirect to creator profile page if the creation is not released yet
    // because we have to publish a LP that contains unreleased creation links
    const isNotReleasedYet =
      creation.reservedAt && isFuture(parseISO(creation.reservedAt));

    if (isNotReleasedYet) {
      return {
        redirect: {
          destination: routes.creatorDetail(creation.creator.userName),
          permanent: false,
        },
        revalidate: 60,
      };
    }

    const creationStatus = getCreationStatus(creation);

    const token =
      creation.aniftyTokenID && creationStatus === 'marketplaceV1'
        ? await fetchTokenByID(creation.aniftyTokenID)
        : null;

    return {
      props: {
        status: 'success',
        creation,
        token,
        ...(await serverSideTranslations(locale, ['common'])),
      },
      revalidate: 60,
    };
  } catch (err) {
    captureException(err);
    return {
      props: {
        status: 'error',
        errorCode: isAxiosError(err) ? err.response.status : 500,
        ...(await serverSideTranslations(locale, ['common'])),
      },
      revalidate: 60,
    };
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

const CreationDetailPage: NextPage<Props> = (props: Props) => {
  const { i18n } = useTranslation();
  const isJA = i18n.language === 'ja';

  const { currentAccountAddress } = useWalletAddress();
  const tryToAuthorize = useTryToAuthorize();

  const initialCreation =
    props.status === 'success' ? props.creation : undefined;
  const { data: creation } = useV2FetchCreationByID(
    initialCreation?.creationID,
    { initialData: initialCreation }
  );

  const isCreator = currentAccountAddress === creation?.creator?.userID;

  const { data: isLiked, isLoading: isLikedLoading } = useFetchCreationIsLiked(
    isCreator ? undefined : creation?.creationID
  );
  const { mutate: toggleIsLiked } = useToggleMyLike();

  if (props.status === 'error') {
    return <Error statusCode={props.errorCode} />;
  }

  // Prepare SSR data
  const title = (isJA && creation.titleJA) || creation.title;
  const description = (isJA && creation.descriptionJA) || creation.description;
  const ogpImageURL = convertImgixURL(creation.imageURL, {
    w: 1200,
  });

  const onLikeButtonClick = async () => {
    if (!creation) return;

    const isAuthorized = await tryToAuthorize({
      redirectTarget: redirectTargets.creationDetail(creation.creationID),
    });
    if (!isAuthorized) return;

    toggleIsLiked(creation.creationID);
  };

  return (
    <>
      <Head>
        <title>{title} | ANIFTY</title>
        <meta property="description" key="description" content={description} />
        <meta property="og:title" key="og:title" content={title} />
        <meta
          property="og:description"
          key="og:description"
          content={description}
        />
        <meta property="og:image" key="og:image" content={ogpImageURL} />
        <meta
          name="twitter:card"
          key="twitter:card"
          content="summary_large_image"
        />
      </Head>

      <CreationDetail
        creation={creation}
        token={props.token}
        isLiked={isLiked}
        isLikedLoading={isLikedLoading}
        onLikeButtonClick={onLikeButtonClick}
      />
    </>
  );
};

export default CreationDetailPage;
